import { useState, useId, useRef } from "react";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";
import { SERVICES } from "./constants";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name").max(100),
  phone: z.string().trim().min(7, "Please enter a valid phone").max(30),
  email: z.string().trim().email("Please enter a valid email").max(255),
  address: z.string().trim().min(5, "Please enter your property address").max(200),
  service: z.string().trim().min(1, "Please choose a service").max(100),
  message: z.string().trim().max(1000).optional().or(z.literal("")),
});

export function ContactForm({
  defaultService,
  formName = "contact_form",
}: {
  defaultService?: string;
  formName?: string;
}) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const formId = useId();
  const id = (name: string) => `${formId}-${name}`;
  const submittedRef = useRef(false);
  const navigate = useNavigate();

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submittedRef.current) return;
    const data = Object.fromEntries(new FormData(e.currentTarget));
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setServerError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/public/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...parsed.data,
          pageUrl: typeof window !== "undefined" ? window.location.href : "",
        }),
      });
      const json = (await res.json().catch(() => ({}))) as { ok?: boolean };
      if (!res.ok || !json.ok) {
        setServerError(
          "Something went wrong. Please call us directly at (352) 901-3094."
        );
        setSubmitting(false);
        return;
      }
      // Server confirmed lead was received. Fire tracking events exactly once.
      submittedRef.current = true;
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        // Google Ads conversion (existing).
        window.gtag("event", "conversion", {
          send_to: "AW-18330308052/aC8UCLHd5tMcENSbyaRE",
        });
        // GA4 recommended lead event — no PII.
        window.gtag("event", "generate_lead", {
          form_name: formName,
          page_location: window.location.href,
          service_category: parsed.data.service,
        });
      }
      navigate({ to: "/thank-you" });
    } catch {
      setServerError(
        "Something went wrong. Please call us directly at (352) 901-3094."
      );
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="rounded-xl border border-border bg-white p-5 md:p-7 shadow-[var(--shadow-card)] space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <Field id={id("name")} label="Your Name" name="name" error={errors.name} required autoComplete="name" />
        <Field id={id("phone")} label="Phone Number" name="phone" type="tel" error={errors.phone} required autoComplete="tel" />
      </div>
      <Field id={id("email")} label="Email" name="email" type="email" error={errors.email} required autoComplete="email" />
      <Field id={id("address")} label="Property Address" name="address" error={errors.address} required autoComplete="street-address" />
      <div>
        <label htmlFor={id("service")} className="block text-sm font-medium mb-1.5 text-foreground">
          Service Needed <span className="text-destructive" aria-hidden="true">*</span>
        </label>
        <select
          id={id("service")}
          name="service"
          defaultValue={defaultService ?? ""}
          required
          aria-required="true"
          aria-invalid={errors.service ? true : undefined}
          aria-describedby={errors.service ? id("service-error") : undefined}
          className="w-full min-h-12 rounded-md border border-input bg-white px-3 py-2 text-base text-foreground"
        >
          <option value="" disabled>Select a roofing service</option>
          {SERVICES.map(s => <option key={s.slug} value={s.title}>{s.title}</option>)}
          <option value="Other / Not Sure">Other / Not sure</option>
        </select>
        {errors.service && (
          <p id={id("service-error")} className="text-sm text-destructive mt-1" role="alert">{errors.service}</p>
        )}
      </div>
      <div>
        <label htmlFor={id("message")} className="block text-sm font-medium mb-1.5 text-foreground">Message <span className="text-muted-foreground text-xs">(optional)</span></label>
        <textarea
          id={id("message")}
          name="message"
          rows={4}
          maxLength={1000}
          placeholder="Tell us about your roof — age, leaks, storm damage, etc."
          className="w-full rounded-md border border-input bg-white px-3 py-2 text-base text-foreground placeholder:text-muted-foreground"
        />
      </div>
      <button type="submit" disabled={submitting} className="btn-cta w-full text-base disabled:opacity-70">
        {submitting ? "Sending…" : "Request My Free Roof Inspection"}
      </button>
      {serverError && (
        <p className="text-sm text-destructive text-center" role="alert" aria-live="polite">{serverError}</p>
      )}
      <p className="text-xs text-muted-foreground text-center">
        By submitting, you agree to be contacted about your roofing project. No spam. Local family-owned service.
      </p>
    </form>
  );
}

function Field({
  id, label, name, type = "text", error, required, autoComplete,
}: { id: string; label: string; name: string; type?: string; error?: string; required?: boolean; autoComplete?: string }) {
  const errorId = `${id}-error`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-1.5 text-foreground">
        {label} {required && <span className="text-destructive" aria-hidden="true">*</span>}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        autoComplete={autoComplete}
        required={required}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        maxLength={type === "email" ? 255 : 200}
        className="w-full min-h-12 rounded-md border border-input bg-white px-3 py-2 text-base text-foreground placeholder:text-muted-foreground"
      />
      {error && <p id={errorId} className="text-sm text-destructive mt-1" role="alert">{error}</p>}
    </div>
  );
}
